import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Popper,
  Paper,
  Button,
  Typography,
  ClickAwayListener,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDownRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import InfoOutlined from "@mui/icons-material/InfoRounded";
import { useTheme } from "@mui/material";
import { HtmlTooltip } from "components/HeatMap";
import {
  POPPER_CONFIG,
  POPPER_WIDTHS,
  PAPER_CONFIG,
  SPACING,
} from "../constants/filterSelectors";

const TasksFilterSelector = ({
  label = "Filters",
  priorities = [],
  entityServiceTypes = [
    "Interpretation",
    "Translation",
    "Other Service",
    "Client",
    "Linguist",
    "Invoice",
  ],
  selectedDueDates = [],
  selectedPriorities = [],
  selectedEntityServiceTypes = [],
  showAllCompleted = false,
  showAutomatedTasks = true,
  selectedMember = "all",
  currentUserId = null,
  onChange,
  onSave,
  onPriorityInfoClick,
  iconOnly = false,
  ...otherProps
}) => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const [anchorEl, setAnchorEl] = useState(null);

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Urgent":
        return theme.palette.error[500];
      case "High":
        return theme.palette.warning[500];
      case "Normal":
        return theme.palette.primary.main;
      case "Low":
        return theme.palette.grey[500];
      default:
        return theme.palette.grey[500];
    }
  };

  // Set defaults: nothing checked except All Members
  const [localSelectedDueDates, setLocalSelectedDueDates] = useState(
    selectedDueDates || [],
  );
  const [localSelectedPriorities, setLocalSelectedPriorities] = useState(
    selectedPriorities || [],
  );
  const [localSelectedEntityServiceTypes, setLocalSelectedEntityServiceTypes] =
    useState(selectedEntityServiceTypes || []);
  const [localShowAllCompleted, setLocalShowAllCompleted] =
    useState(showAllCompleted);
  const [localShowAutomatedTasks, setLocalShowAutomatedTasks] =
    useState(showAutomatedTasks);
  const [localSelectedMember, setLocalSelectedMember] =
    useState(selectedMember);
  const textFieldRef = useRef(null);

  // Update local state when props change
  useEffect(() => {
    setLocalSelectedDueDates(selectedDueDates || []);
    setLocalSelectedPriorities(selectedPriorities || []);
    setLocalSelectedEntityServiceTypes(selectedEntityServiceTypes || []);
    setLocalShowAllCompleted(showAllCompleted);
    setLocalShowAutomatedTasks(showAutomatedTasks);
    setLocalSelectedMember(selectedMember);
  }, [
    selectedDueDates,
    selectedPriorities,
    selectedEntityServiceTypes,
    showAllCompleted,
    showAutomatedTasks,
    selectedMember,
  ]);

  const handleClick = () => {
    setAnchorEl(anchorEl ? null : textFieldRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSave = () => {
    const filters = {
      dueDates: [...localSelectedDueDates],
      priorities: [...localSelectedPriorities],
      entityServiceTypes: [...localSelectedEntityServiceTypes],
      showAllCompleted: localShowAllCompleted,
      showAutomatedTasks: localShowAutomatedTasks,
      selectedMember: localSelectedMember,
    };

    if (onSave) {
      onSave(filters);
    } else if (onChange) {
      onChange(filters);
    }
    handleClose();
  };

  const handleDueDateToggle = (dueDate) => {
    setLocalSelectedDueDates((prev) => {
      // Pick one only - if already selected, deselect it, otherwise select only this one
      if (prev.includes(dueDate)) {
        return [];
      } else {
        return [dueDate];
      }
    });
  };

  const handlePriorityToggle = (priority) => {
    setLocalSelectedPriorities((prev) => {
      if (prev.includes(priority)) {
        return prev.filter((i) => i !== priority);
      } else {
        return [...prev, priority];
      }
    });
  };

  const handleEntityServiceTypeToggle = (type) => {
    setLocalSelectedEntityServiceTypes((prev) => {
      if (prev.includes(type)) {
        return prev.filter((i) => i !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const getDisplayText = () => {
    const filterCount =
      localSelectedDueDates.length +
      localSelectedPriorities.length +
      localSelectedEntityServiceTypes.length +
      (localShowAllCompleted ? 1 : 0) +
      (!localShowAutomatedTasks ? 1 : 0);
    // Member filter (All Members/Just Me) does NOT count towards filter count

    if (filterCount === 0) {
      return "No filters applied";
    }

    return `${filterCount} filter${filterCount > 1 ? "s" : ""} applied`;
  };

  const handleClearFilter = () => {
    // Reset to empty - no filters
    setLocalSelectedDueDates([]);
    setLocalSelectedPriorities([]);
    setLocalSelectedEntityServiceTypes([]);
    setLocalShowAllCompleted(false);
    setLocalShowAutomatedTasks(true);
    setLocalSelectedMember("all");

    const filters = {
      dueDates: [],
      priorities: [],
      entityServiceTypes: [],
      showAllCompleted: false,
      showAutomatedTasks: true,
      selectedMember: "all",
    };

    if (onSave) {
      onSave(filters);
    } else if (onChange) {
      onChange(filters);
    }
    handleClose();
  };

  const open = Boolean(anchorEl);
  const hasActiveFilter =
    localSelectedDueDates.length > 0 ||
    localSelectedPriorities.length > 0 ||
    localSelectedEntityServiceTypes.length > 0 ||
    localShowAllCompleted ||
    !localShowAutomatedTasks;
  // Member filter does NOT count as active filter

  return (
    <Box
      sx={{ position: "relative", width: iconOnly ? "auto" : "100%" }}
      {...otherProps}
    >
      {iconOnly ? (
        <Tooltip title={label}>
          <IconButton
            ref={textFieldRef}
            onClick={handleClick}
            size="small"
            sx={{
              width: "40px",
              height: "40px",
              backgroundColor: theme.palette.background.default,
              boxShadow:
                "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
              borderRadius: "12px",
              border: "none",
              color: hasActiveFilter
                ? theme.palette.info.main
                : theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.buttonHover,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <FilterListRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <TextField
          ref={textFieldRef}
          fullWidth
          size="small"
          label={label}
          //variant="filled"
          value={getDisplayText()}
          onClick={handleClick}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none !important",
            },
            "& .MuiFilledInput-root": {
              borderRadius: "10px",
            },
            "& .MuiFilledInput-underline:after": {
              borderBottom: "none !important",
            },
            "& .MuiFilledInput-underline:before": {
              borderBottom: "none !important",
            },
            height: "100%",
            "& .MuiInputBase-root": {
              height: "100%",
              display: "flex",
              alignItems: "center",
              boxShadow:
                "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
              borderRadius: "12px",
            },
            "& .MuiInputBase-input": {
              py: "8px !important",
              color: hasActiveFilter ? theme.palette.info.main : "inherit",
            },
          }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton
                edge="end"
                onClick={handleClick}
                sx={{ padding: 0.5 }}
              >
                <ArrowDropDownIcon />
              </IconButton>
            ),
          }}
        />
      )}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{
          zIndex: POPPER_CONFIG.zIndex,
          width: isMobile ? "calc(100vw - 32px)" : POPPER_WIDTHS.tasks,
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={PAPER_CONFIG.elevation}
            sx={{
              p: PAPER_CONFIG.padding,
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.background.default}`,
              borderRadius: PAPER_CONFIG.borderRadius,
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <>
              {/* Header with Priority Info Icon */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: SPACING.containerGap,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.primary.text2,
                  }}
                >
                  Filters
                </Typography>
                {onPriorityInfoClick && (
                  <HtmlTooltip
                    title={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            mb: 0.5,
                          }}
                        >
                          Priority Color Codes
                        </Typography>
                        {["Urgent", "High", "Normal", "Low"].map((priority) => (
                          <Box
                            key={priority}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: "16px",
                                height: "16px",
                                borderRadius: "4px",
                                bgcolor: getPriorityColor(priority),
                              }}
                            />
                            <Typography variant="body2">{priority}</Typography>
                          </Box>
                        ))}
                      </Box>
                    }
                    arrow
                  >
                    <IconButton
                      size="small"
                      sx={{ color: theme.palette.grey[500] }}
                    >
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </HtmlTooltip>
                )}
              </Box>

              {/* Main Grid Layout - Row 1: Due Date | Types */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: SPACING.containerGap,
                  mb: SPACING.containerGap,
                }}
              >
                {/* Left Column */}
                <Box sx={{ flex: isMobile ? "1" : "0 0 48%" }}>
                  {/* Due Date Section */}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        fontWeight: "bold",
                        color: theme.palette.primary.text2,
                      }}
                    >
                      Due Date
                    </Typography>

                    <Grid container spacing={1}>
                      {[
                        "Overdue",
                        "Today",
                        "Tomorrow",
                        "This Week",
                        "Next Week",
                        "This Month",
                      ].map((dueDate) => {
                        const isSelected =
                          localSelectedDueDates.includes(dueDate);
                        return (
                          <Grid size={{ xs: 6 }} key={dueDate}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                p: 0.75,
                                borderRadius: "12px",
                                cursor: "pointer",
                                border: `2px solid ${theme.palette.primary.main}`,
                                bgcolor: isSelected
                                  ? theme.palette.primary.main
                                  : "transparent",
                                "&:hover": {
                                  bgcolor: isSelected
                                    ? theme.palette.primary.buttonHoverSelected
                                    : theme.palette.primary.buttonHover,
                                },
                              }}
                              onClick={() => handleDueDateToggle(dueDate)}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.75rem",
                                  color: isSelected
                                    ? theme.palette.background.default
                                    : theme.palette.primary.text2,
                                  fontWeight: isSelected ? "bold" : "normal",
                                }}
                              >
                                {dueDate}
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </Box>

                {/* Vertical Divider */}
                {!isMobile && <Divider orientation="vertical" flexItem />}

                {/* Right Column - Types */}
                <Box sx={{ flex: isMobile ? "1" : "0 0 48%" }}>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        fontWeight: "bold",
                        color: theme.palette.primary.text2,
                      }}
                    >
                      Types
                    </Typography>

                    <Grid container spacing={1}>
                      {entityServiceTypes.map((type) => {
                        const isSelected =
                          localSelectedEntityServiceTypes.includes(type);
                        return (
                          <Grid size={{ xs: 6 }} key={type}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                p: 0.75,
                                borderRadius: "12px",
                                cursor: "pointer",
                                border: `2px solid ${theme.palette.primary.main}`,
                                bgcolor: isSelected
                                  ? theme.palette.primary.main
                                  : "transparent",
                                "&:hover": {
                                  bgcolor: isSelected
                                    ? theme.palette.primary.buttonHoverSelected
                                    : theme.palette.primary.buttonHover,
                                },
                              }}
                              onClick={() =>
                                handleEntityServiceTypeToggle(type)
                              }
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.75rem",
                                  color: isSelected
                                    ? theme.palette.background.default
                                    : theme.palette.primary.text2,
                                  fontWeight: isSelected ? "bold" : "normal",
                                }}
                              >
                                {type}
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Row 2: Show Tasks For | Priorities */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: SPACING.containerGap,
                  mb: SPACING.containerGap,
                }}
              >
                {/* Left Column - Show Tasks For */}
                <Box sx={{ flex: isMobile ? "1" : "0 0 48%" }}>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        fontWeight: "bold",
                        color: theme.palette.primary.text2,
                      }}
                    >
                      Show Tasks For:
                    </Typography>

                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            p: 0.75,
                            borderRadius: "12px",
                            cursor: "pointer",
                            border: `2px solid ${theme.palette.primary.main}`,
                            bgcolor:
                              localSelectedMember === "all"
                                ? theme.palette.primary.main
                                : "transparent",
                            "&:hover": {
                              bgcolor:
                                localSelectedMember === "all"
                                  ? theme.palette.primary.buttonHoverSelected
                                  : theme.palette.primary.buttonHover,
                            },
                          }}
                          onClick={() => setLocalSelectedMember("all")}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.75rem",
                              color:
                                localSelectedMember === "all"
                                  ? theme.palette.background.default
                                  : theme.palette.primary.text2,
                              fontWeight:
                                localSelectedMember === "all"
                                  ? "bold"
                                  : "normal",
                            }}
                          >
                            All Members
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            p: 0.75,
                            borderRadius: "12px",
                            cursor: "pointer",
                            border: `2px solid ${theme.palette.primary.main}`,
                            bgcolor:
                              localSelectedMember === "me"
                                ? theme.palette.primary.main
                                : "transparent",
                            "&:hover": {
                              bgcolor:
                                localSelectedMember === "me"
                                  ? theme.palette.primary.buttonHoverSelected
                                  : theme.palette.primary.buttonHover,
                            },
                          }}
                          onClick={() => setLocalSelectedMember("me")}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.75rem",
                              color:
                                localSelectedMember === "me"
                                  ? theme.palette.background.default
                                  : theme.palette.primary.text2,
                              fontWeight:
                                localSelectedMember === "me"
                                  ? "bold"
                                  : "normal",
                            }}
                          >
                            Only Me
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                {/* Vertical Divider */}
                {!isMobile && <Divider orientation="vertical" flexItem />}

                {/* Right Column - Priorities */}
                <Box sx={{ flex: isMobile ? "1" : "0 0 48%" }}>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        fontWeight: "bold",
                        color: theme.palette.primary.text2,
                      }}
                    >
                      Priorities
                    </Typography>

                    <Grid container spacing={1}>
                      {priorities.map((priority) => {
                        const isSelected =
                          localSelectedPriorities.includes(priority);
                        const getPriorityColor = (priority) => {
                          switch (priority) {
                            case "Urgent":
                              return theme.palette.error.main;
                            case "High":
                              return theme.palette.warning.main;
                            case "Normal":
                              return theme.palette.primary.main;
                            case "Low":
                              return theme.palette.grey[500];
                            default:
                              return theme.palette.grey[500];
                          }
                        };
                        return (
                          <Grid size={{ xs: 6 }} key={priority}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 1,
                                p: 0.75,
                                borderRadius: "12px",
                                cursor: "pointer",
                                border: `2px solid ${theme.palette.primary.main}`,
                                bgcolor: isSelected
                                  ? theme.palette.primary.main
                                  : "transparent",
                                "&:hover": {
                                  bgcolor: isSelected
                                    ? theme.palette.primary.buttonHoverSelected
                                    : theme.palette.primary.buttonHover,
                                },
                              }}
                              onClick={() => handlePriorityToggle(priority)}
                            >
                              <Box
                                sx={{
                                  width: "12px",
                                  height: "12px",
                                  borderRadius: "3px",
                                  bgcolor: getPriorityColor(priority),
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.75rem",
                                  color: isSelected
                                    ? theme.palette.background.default
                                    : theme.palette.primary.text2,
                                  fontWeight: isSelected ? "bold" : "normal",
                                }}
                              >
                                {priority}
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Options Section */}
              <Box mb={2}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1.5,
                    fontWeight: "bold",
                    color: theme.palette.primary.text2,
                  }}
                >
                  Options:
                </Typography>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={localShowAllCompleted}
                        onChange={(e) =>
                          setLocalShowAllCompleted(e.target.checked)
                        }
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          sx={{
                            color: theme.palette.primary.text2,
                            fontSize: "0.875rem",
                          }}
                        >
                          Show All Completed Assignments
                        </Typography>
                        <Typography
                          sx={{
                            color: theme.palette.grey[400],
                            fontSize: "0.75rem",
                          }}
                        >
                          {localShowAllCompleted
                            ? "Shows all completed tasks"
                            : "Shows completed tasks from past 7 days"}
                        </Typography>
                      </Box>
                    }
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={localShowAutomatedTasks}
                        onChange={(e) =>
                          setLocalShowAutomatedTasks(e.target.checked)
                        }
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          sx={{
                            color: theme.palette.primary.text2,
                            fontSize: "0.875rem",
                          }}
                        >
                          Show Automated Tasks
                        </Typography>
                        <Typography
                          sx={{
                            color: theme.palette.grey[400],
                            fontSize: "0.75rem",
                          }}
                        >
                          {localShowAutomatedTasks
                            ? "Includes tasks generated by automation rules"
                            : "Excludes auto-generated tasks"}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {hasActiveFilter ? (
                  <Button
                    onClick={handleClearFilter}
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<ClearIcon />}
                  >
                    Clear All Filters
                  </Button>
                ) : (
                  <Box />
                )}

                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.background.default,
                    borderRadius: "12px",
                    "&:hover": {
                      bgcolor: theme.palette.primary.buttonHoverSelected,
                    },
                  }}
                >
                  Apply Filters
                </Button>
              </Box>
            </>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default TasksFilterSelector;
